import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PlanOverview.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkoutItem {
  id: string;
  nanoid: string;
  name: string;
  modality: string;
  duration_minutes: number | null;
  difficulty: string | null;
  exercise_count: number;
  slot_number: number;
  completed_at: string | null;
  slot_notes: string | null;
}

interface PlanDay {
  day_number: number;
  workouts: WorkoutItem[];
}

interface PlanWeek {
  week_number: number;
  days: PlanDay[];
}

interface Plan {
  id: string;
  nanoid: string;
  name: string;
  description: string | null;
  focus: string | null;
  duration_weeks: number | null;
  created_at: string;
}

interface PlanStats {
  total_training_days: number;
  completed_days: number;
  current_week: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MODALITY_LABELS: Record<string, string> = {
  running:     'Run',
  strength:    'Strength',
  yoga:        'Yoga',
  mobility:    'Mobility',
  hiit:        'HIIT',
  cardio:      'Cardio',
  stretch:     'Stretch',
  core:        'Core',
  group_class: 'Class',
  other:       'Activity',
};

function modalityLabel(modality: string): string {
  return MODALITY_LABELS[modality] ?? modality;
}

function progressPct(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function WeekTab({
  week,
  isCurrent,
  isSelected,
  onClick,
}: {
  week: PlanWeek;
  isCurrent: boolean;
  isSelected: boolean;
  onClick: () => void;
}) {
  const completedCount = week.days.reduce((sum, d) => sum + d.workouts.filter(w => w.completed_at).length, 0);
  const totalCount     = week.days.reduce((sum, d) => sum + d.workouts.length, 0);

  return (
    <button
      className={`po-week-tab${isSelected ? ' po-week-tab--active' : ''}${isCurrent ? ' po-week-tab--current' : ''}`}
      onClick={onClick}
    >
      <span className="po-week-tab-label">Week {week.week_number}</span>
      {totalCount > 0 && (
        <span className="po-week-tab-count">
          {completedCount}/{totalCount}
        </span>
      )}
    </button>
  );
}

function WorkoutItemRow({ item }: { item: WorkoutItem }) {
  const isDone       = Boolean(item.completed_at);
  const durationStr  = item.duration_minutes ? `${item.duration_minutes} min` : null;
  const exerciseStr  = item.exercise_count === 1 ? '1 exercise' : `${item.exercise_count} exercises`;

  return (
    <a
      href={`/workout/${item.nanoid}`}
      className={`po-workout-row${isDone ? ' po-workout-row--done' : ''}`}
    >
      <div className="po-workout-row-left">
        <span className="po-workout-row-name">{item.name}</span>
        <span className="po-workout-row-meta">
          {modalityLabel(item.modality)}
          {durationStr ? ` · ${durationStr}` : ''}
          {` · ${exerciseStr}`}
        </span>
      </div>
      <div className="po-workout-row-right">
        {isDone
          ? <span className="po-day-check">&#10003;</span>
          : <span className="po-day-arrow">&#8250;</span>
        }
      </div>
    </a>
  );
}

function DayCard({ day, isCurrentWeek }: { day: PlanDay; isCurrentWeek: boolean }) {
  const dayName = DAY_NAMES[day.day_number] ?? `Day ${day.day_number}`;

  if (day.workouts.length === 0) {
    return (
      <div className="po-day-card po-day-card--rest">
        <span className="po-day-label">{dayName}</span>
        <span className="po-day-rest-text">Rest</span>
      </div>
    );
  }

  const allDone = day.workouts.every(w => w.completed_at);

  return (
    <div className={`po-day-card${allDone ? ' po-day-card--done' : ''}${isCurrentWeek && !allDone ? ' po-day-card--upcoming' : ''}`}>
      <span className="po-day-label po-day-label--multi">{dayName}</span>
      {day.workouts.map(item => (
        <WorkoutItemRow key={item.id} item={item} />
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PlanOverview() {
  const { nanoid } = useParams<{ nanoid: string }>();
  const navigate = useNavigate();

  const [plan,  setPlan]  = useState<Plan | null>(null);
  const [weeks, setWeeks] = useState<PlanWeek[]>([]);
  const [stats, setStats] = useState<PlanStats | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!nanoid) return;
    fetch(`/api/plans/${nanoid}`)
      .then(r => r.json())
      .then(data => {
        if (!data.success) throw new Error(data.error || 'Failed to load plan');
        setPlan(data.plan);
        setWeeks(data.weeks);
        setStats(data.stats);
        const cw = data.stats?.current_week ?? 1;
        const maxW = data.weeks.length > 0 ? data.weeks[data.weeks.length - 1].week_number : 1;
        setSelectedWeek(Math.min(cw, maxW));
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [nanoid]);

  if (loading) {
    return (
      <div className="po-page">
        <div className="po-state-screen">
          <div className="po-spinner" />
          <p className="po-state-msg">Loading your plan...</p>
        </div>
      </div>
    );
  }

  if (error || !plan || !stats) {
    return (
      <div className="po-page">
        <div className="po-state-screen">
          <p className="po-state-msg po-state-error">{error || 'Plan not found.'}</p>
        </div>
      </div>
    );
  }

  const pct         = progressPct(stats.completed_days, stats.total_training_days);
  const activeWeek  = weeks.find(w => w.week_number === selectedWeek);
  const currentWeek = stats.current_week;

  return (
    <div className="po-page">
      <div className="po-root">
        <div className="po-container">

          {/* ── Header ────────────────────────────────────────────────── */}
          <div className="po-header">
            {plan.focus && (
              <span className="po-badge">{plan.focus.toUpperCase()}</span>
            )}
            <div className="po-header-row">
              <h1 className="po-title">{plan.name}</h1>
              <button
                className="po-edit-btn"
                onClick={() => navigate(`/plan/${plan.nanoid}/edit`)}
              >
                Edit Plan
              </button>
            </div>
            {plan.description && (
              <p className="po-subtitle">{plan.description}</p>
            )}
          </div>

          {/* ── Progress bar ───────────────────────────────────────────── */}
          <div className="po-progress-section">
            <div className="po-progress-bar">
              <div className="po-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="po-progress-label">
              {stats.completed_days} of {stats.total_training_days} sessions complete
            </p>
          </div>

          {/* ── Week tabs ──────────────────────────────────────────────── */}
          <div className="po-week-tabs">
            {weeks.map(week => (
              <WeekTab
                key={week.week_number}
                week={week}
                isCurrent={week.week_number === currentWeek}
                isSelected={week.week_number === selectedWeek}
                onClick={() => setSelectedWeek(week.week_number)}
              />
            ))}
          </div>

          {/* ── Selected week detail ───────────────────────────────────── */}
          {activeWeek && (
            <div className="po-week-detail">
              <div className="po-week-heading">
                <span className="po-week-heading-label">
                  Week {activeWeek.week_number}
                  {activeWeek.week_number === currentWeek ? ' — Current' : ''}
                </span>
                {plan.duration_weeks && (
                  <span className="po-week-of">of {plan.duration_weeks}</span>
                )}
              </div>
              <div className="po-day-list">
                {activeWeek.days.map(day => (
                  <DayCard
                    key={day.day_number}
                    day={day}
                    isCurrentWeek={activeWeek.week_number === currentWeek}
                  />
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="po-footer">
        <div className="po-footer-inner">
          <span className="po-footer-brand">Mosaime</span>
          <p className="po-footer-copy">&copy; {new Date().getFullYear()} Mosaime. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
